import { Injectable } from '@angular/core';
import { isNullOrUndefined, MessageService } from '@sla-ng/core';
import { ICostType } from '../../../models/cost-type.model';
import { FieldType, IField } from '@sla-ng/ui-material';
import * as moment from 'moment';
import { BehaviorSubject, firstValueFrom, Observable, of } from 'rxjs';

import { ICostAccountingType } from '../../../models/cost-accounting-type.model';
import { ICostCenterApportionmentUpdate } from '../../../models/cost-center-apportionment-update.model';

import { ICostCenterApportionment } from '../../../models/cost-center-apportionment.model';
import { ICostCenter } from '../../../models/cost-center.model';
import { IReference } from '../../../models/reference.model';
import { CostAccountingTypeResource } from '../../../resources/cost-accounting-type.resource';
import { CostCenterResource } from '../../../resources/cost-center.resource';
import { CostTypeResource } from '../../../resources/cost-type.resource';
import { ReferenceResource } from '../../../resources/reference.resource';
import { IBabState } from './bab-state.model';

let _state: IBabState = {
    apportionmentColumns: [ 'receivingCostCenterName', 'secondaryCostType', 'placeholder', 'validFrom', 'validTo', 'actions' ],
    apportionments: [],
    costCenterLookup: {},
    costTypeLookup: {},
    dialogAddFields: [],
    dialogEditFields: [],
    isLoading: false,
    selectFields: [],
    selectedApportionmentReference: null,
    selectedCostAccountingType: null,
    selectedCostCenter: null,
    selectedValidFrom: null,
    selectedValidTo: null,
};

@Injectable({
    providedIn: 'root',
})
export class BabFacade {

    protected store$: BehaviorSubject<IBabState> = new BehaviorSubject<IBabState>(_state);

    constructor(private myCostCenterResource: CostCenterResource,
                private myReferenceResource: ReferenceResource,
                private myCostAccountingTypeResource: CostAccountingTypeResource,
                private myCostTypeResource: CostTypeResource,
                private myMessageService: MessageService) {
    }

    public getStateSnapshot(): IBabState {
        return this.store$.value;
    }

    public clearState(): void {
        this.updateState({
            apportionmentColumns: [ 'receivingCostCenterName', 'secondaryCostType', 'placeholder', 'validFrom', 'validTo', 'actions' ],
            apportionments: [],
            costCenterLookup: {},
            costTypeLookup: {},
            dialogAddFields: [],
            dialogEditFields: [],
            isLoading: false,
            selectFields: [],
            selectedApportionmentReference: null,
            selectedCostAccountingType: null,
            selectedCostCenter: null,
            selectedValidFrom: null,
            selectedValidTo: null,
        });
    }

    public async addApportionment(apportionment: ICostCenterApportionment): Promise<void> {
        this.setIsloading(true);
        const state: IBabState = this.getStateSnapshot();
        const createdApportionment: ICostCenterApportionment = await this.myCostCenterResource
            .addCostCenterApportionment(state.selectedCostCenter, apportionment).toPromise().catch(() => null);
        if (createdApportionment !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center-apportionment.actions.added.label');
        }
        this.setIsloading(false);
    }

    public async deleteApportionment(apportionment: ICostCenterApportionment): Promise<void> {
        this.setIsloading(true);
        const state: IBabState = this.getStateSnapshot();
        try {
            await this.myCostCenterResource.deleteCostCenterCostApportionment(state.selectedCostCenter, apportionment.id).toPromise();
            this.myMessageService.successTrans('app.actions.feedback.deleted', 'app.page.cost-center-apportionment.actions.deleted.label');
        } catch (error) {
            console.error(error);
        }
        this.setIsloading(false);
    }

    public async updateApportionment(apportionmentId: number, validTo: moment.Moment, costTypeId: number): Promise<void> {
        const state: IBabState = this.getStateSnapshot();
        this.setIsloading(true);
        const updateApportionment: ICostCenterApportionmentUpdate = { validTo, costTypeId };
        const updatedApportionment: ICostCenterApportionment = await this.myCostCenterResource
            .updateCostCenterApportionment(apportionmentId, state.selectedCostCenter, updateApportionment).toPromise().catch(() => null);
        if (updatedApportionment !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center-apportionment.actions.edited.label');
        }
        this.setIsloading(false);
    }

    public async fetchData(): Promise<void> {
        this.setIsloading(true);

        // Load data concurrently
        const [ references, costAccountingTypes, costCenters, secondaryCostTypes ]: [ IReference[], ICostAccountingType[], ICostCenter[], ICostType[] ] = await Promise.all([
            firstValueFrom(this.myReferenceResource.getAllReferences()).catch(() => []),
            firstValueFrom(this.myCostAccountingTypeResource.getAllCostAccountingTypes()).catch(() => []),
            firstValueFrom(this.myCostCenterResource.getCostCenters()).catch(() => []),
            firstValueFrom(this.myCostTypeResource.getCostTypes(false, true, false)).catch(() => []),
        ]);

        this.setInputsFields(references, costAccountingTypes, costCenters, secondaryCostTypes);
        this.buildLookupMaps(costCenters, secondaryCostTypes);
        this.setIsloading(false);
    }

    public async fetchApportionmentsIfFiltersAreValid(): Promise<void> {
        const state: IBabState = this.getStateSnapshot();
        if (!isNullOrUndefined(state.selectedCostCenter)
            && !isNullOrUndefined(state.selectedCostAccountingType)
            && !isNullOrUndefined(state.selectedValidFrom)
            && !isNullOrUndefined(state.selectedValidTo)) {
            this.setIsloading(true);
            const apportionments: ICostCenterApportionment[] = await this.myCostCenterResource
                .getCostCenterApportionments(state.selectedCostCenter, state.selectedCostAccountingType, state.selectedValidFrom, state.selectedValidTo).toPromise().catch(() => []);
            this.updateState({ ..._state, apportionments });
            this.setIsloading(false);
        }
    }

    public subState(): Observable<IBabState> {
        return this.store$.asObservable();
    }

    public async setSelectedKostenstelle(selectedCostCenter: number): Promise<void> {
        this.updateState({ ..._state, selectedCostCenter });
        await Promise.all([ this.fetchApportionmentsIfFiltersAreValid(), this.fetchApportionmentReference() ]);
        this.filterCostCenterSelectionOptions();
    }

    public async setSelectedCostAccountingType(selectedCostAccountingType: number): Promise<void> {
        this.updateState({ ..._state, selectedCostAccountingType });
        await Promise.all([ this.fetchApportionmentsIfFiltersAreValid(), this.fetchApportionmentReference() ]);
    }

    public async setSelectedDaterange(selectedValidFrom: moment.Moment, selectedValidTo: moment.Moment): Promise<void> {
        this.updateState({ ..._state, selectedValidFrom });
        this.updateState({ ..._state, selectedValidTo });
        await this.fetchApportionmentsIfFiltersAreValid();
    }

    public async setSelectedApportionmentReference(apportionmentReferenceId: number): Promise<void> {
        const state: IBabState = this.getStateSnapshot();
        this.setIsloading(true);
        try {
            await this.myCostCenterResource.updateApportionmentReference(state.selectedCostCenter, apportionmentReferenceId, state.selectedCostAccountingType).toPromise();
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center-apportionment-reference.actions.edited.label');
            this.updateState({ ..._state, selectedApportionmentReference: apportionmentReferenceId });
        } catch {
            this.updateState({ ..._state, selectedApportionmentReference: null });
        }
        this.setIsloading(false);

    }

    private updateState(state: IBabState): void {
        this.store$.next(_state = state);
    }

    private setIsloading(isLoading: boolean): void {
        this.updateState({ ..._state, isLoading });
    }

    private setInputsFields(references: IReference[], costAccountingTypes: ICostAccountingType[], costCenters: ICostCenter[], secondaryCostTypes: ICostType[]): void {
        const selectFields: IField[] = [
            {
                field: 'selectedCostAccountingType',
                label: 'app.page.join-cost-center-cost-center.costAccountingType.label',
                type: FieldType.select,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of(costAccountingTypes),
                },
            },
            {
                field: 'selectedCostCenter',
                label: 'app.model.join-cost-center-cost-center.emittingCostCenter.label',
                type: FieldType.select,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of(costCenters),
                },
            },
            {
                field: 'selectedDaterange',
                label: 'app.page.join-cost-center-cost-center.daterange.label',
                type: FieldType.daterange,
            },
            {
                field: 'selectedApportionmentReference',
                label: 'app.page.join-cost-center-cost-center.apportionmentReference.label',
                type: FieldType.select,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of(references),
                },
            },
        ];
        this.updateState({ ..._state, selectFields });

        const dialogAddFields: IField[] = [
            {
                field: 'costAccountingTypeId',
                label: 'app.page.join-cost-center-cost-center.costAccountingType.label',
                type: FieldType.select,
                required: true,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of(costAccountingTypes),
                },
            },
            {
                field: 'receivingCostCenterId',
                label: 'app.model.join-cost-center-cost-center.receivingCostCenter.label',
                type: FieldType.select,
                required: true,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of(costCenters),
                },
            },
            {
                field: 'costTypeId',
                label: 'app.model.join-cost-center-cost-center.secondaryCostType.label',
                type: FieldType.select,
                required: true,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of(secondaryCostTypes),
                },
            },
            {
                field: 'validFrom',
                label: 'app.model.join-cost-center-cost-center.validFrom.label',
                type: FieldType.date,
                required: true,
            },
            {
                field: 'validTo',
                label: 'app.model.join-cost-center-cost-center.validTo.label',
                type: FieldType.date,
                required: false,
            },

        ];

        const dialogEditFields: IField[] = [
            {
                field: 'costTypeId',
                label: 'app.model.join-cost-center-cost-center.secondaryCostType.label',
                type: FieldType.select,
                required: true,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of(secondaryCostTypes),
                },
            },
            {
                field: 'validTo',
                label: 'app.model.join-cost-center-cost-center.validTo.label',
                type: FieldType.date,
                required: true,
            },

        ];

        this.updateState({ ..._state, dialogAddFields });
        this.updateState({ ..._state, dialogEditFields });
    }

    private async fetchApportionmentReference(): Promise<void> {
        const state: IBabState = this.getStateSnapshot();
        if (!isNullOrUndefined(state.selectedCostCenter) && !isNullOrUndefined(state.selectedCostAccountingType)) {
            const apportionmentReference: IReference = await this.myCostCenterResource
                .getApportionmentReference(state.selectedCostCenter, state.selectedCostAccountingType).toPromise().catch(() => null);
            isNullOrUndefined(apportionmentReference)
                ? this.updateState({ ..._state, selectedApportionmentReference: null })
                : this.updateState({ ..._state, selectedApportionmentReference: apportionmentReference.id });
        } else {
            this.updateState({ ..._state, selectedApportionmentReference: null });
        }
    }

    private buildLookupMaps(costCenters: ICostCenter[], secondaryCostTypes: ICostType[]): void {
        const costCenterLookup: Record<number, ICostCenter> = {};
        const costTypeLookup: Record<number, ICostType> = {};

        for (const costCenter of costCenters) {
            costCenterLookup[ costCenter.id ] = costCenter;
        }
        for (const costType of secondaryCostTypes) {
            costTypeLookup[ costType.id ] = costType;
        }
        this.updateState({ ..._state, costCenterLookup });
        this.updateState({ ..._state, costTypeLookup });
    }

    private filterCostCenterSelectionOptions(): void {
        // Exclude the selected Emitting CostCenter from the list of possible Receiving CostCenters
        const state: IBabState = this.getStateSnapshot();
        const dialogAddFields: IField[] = state.dialogAddFields;
        let costCenterList: ICostCenter[] = Object.values(state.costCenterLookup);
        if (!isNullOrUndefined(state.selectedCostCenter)) {
            costCenterList = costCenterList.filter(c => c.id !== state.selectedCostCenter);
        }
        dialogAddFields[ 1 ].selectData.options = of(costCenterList);
        this.updateState({ ..._state, dialogAddFields });
    }
}
